// UserActivityLogTable.tsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Card,
} from "@mui/material";
import moment from "moment";
import { makeGETRequest } from "../../Api/api";

const methods = ["GET", "POST", "PUT", "DELETE"];

type UserActivityLog = {
  _id: string;
  timestamp: string;
  method: string;
  path: string;
  status_code: number;
  phone_number: string;
  name: string;
  email: string;
};

type ApiResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserActivityLog[];
};

const UserActivityLogTable = () => {
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(200);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [filters, setFilters] = useState({
    method: "",
    status: "",
    phone: "",
    name: "",
    email: "",
    path: "",
    startDate: "",
    endDate: "",
  });

  const logsRef = useRef<UserActivityLog[]>([]);
  const pageRef = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logs = useMemo(() => {
    return [...logsRef.current];
  }, [updateTrigger]);

  const buildQueryParams = (pageNumber: number) => {
    const params = new URLSearchParams();
    params.append("page", (pageNumber + 1).toString());
    params.append("page_size", rowsPerPage.toString());
    if (filters.phone) params.append("number", filters.phone);
    if (filters.method) params.append("method", filters.method);
    if (filters.status) params.append("status_code", filters.status);
    if (filters.name) params.append("name", filters.name);
    if (filters.email) params.append("email", filters.email);
    if (filters.path) params.append("path", filters.path);
    if (filters.startDate && filters.endDate) {
      params.append("start_date", filters.startDate);
      params.append("end_date", filters.endDate);
    }
    return params.toString();
  };

  const fetchLogs = async (isAppend = false, pageOverride?: number) => {
    const actualPage = pageOverride !== undefined ? pageOverride : page;

    if (loading || isScrollLoading) return;

    isAppend ? setIsScrollLoading(true) : setLoading(true);

    try {
      const queryParams = buildQueryParams(actualPage);
      const response = await makeGETRequest(
        `/admin_activity_logs/user_activity_log_list/?${queryParams}`
      );
      const data: ApiResponse = response.data;

      if (isAppend) {
        const existingIds = new Set(logsRef.current.map((log) => log._id));
        const newLogs = data.results.filter((log) => !existingIds.has(log._id));
        logsRef.current = [...logsRef.current, ...newLogs];
      } else {
        logsRef.current = data.results;
      }

      setTotalCount(data.count || 0);
      setUpdateTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching logs:", error);
      if (!isAppend) {
        logsRef.current = [];
        setTotalCount(0);
        setUpdateTrigger((prev) => prev + 1);
      }
    } finally {
      isAppend ? setIsScrollLoading(false) : setLoading(false);
    }
  };

  const handleScroll = () => {
    if (!containerRef.current || loading || isScrollLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isBottom && logs.length < totalCount) {
      if (pageRef.current === page) {
        pageRef.current += 1;
        setPage((prev) => prev + 1);
      }
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (page > 0) {
      fetchLogs(true);
    }
  }, [page]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
      setPage(0);
      pageRef.current = 0;
      logsRef.current = [];
      setTotalCount(0);
      setUpdateTrigger((prev) => prev + 1);
      setIsScrollLoading(false);
      fetchLogs(false, 0);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [filters]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [logs.length, totalCount, loading, isScrollLoading]);

  const handleFilterChange = (field: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const memoizedRows = useMemo(() => {
    if (logs.length === 0 && !loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            No data found
          </TableCell>
        </TableRow>
      );
    }
    return logs.map((log) => (
      <TableRow key={log._id}>
        <TableCell>
          {moment(log.timestamp).format("DD MMMM YYYY hh:mm A")}
        </TableCell>
        <TableCell>{log.method}</TableCell>
        <TableCell>{log.status_code}</TableCell>
        <TableCell>{log.phone_number}</TableCell>
        <TableCell>{log.name}</TableCell>
        <TableCell>{log.email}</TableCell>
        <TableCell>{log.path}</TableCell>
      </TableRow>
    ));
  }, [logs, loading]);

  return (
    <Card sx={{ padding: "1rem", borderRadius: "1rem" }} variant="outlined">
      <Box p={2}>
        <h2>User Activity Logs</h2>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField
            label="Method"
            select
            value={filters.method}
            onChange={handleFilterChange("method")}
            size="small"
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="">All</MenuItem>
            {methods.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Status Code"
            value={filters.status}
            onChange={handleFilterChange("status")}
            size="small"
          />
          <TextField
            label="Phone Number"
            value={filters.phone}
            onChange={handleFilterChange("phone")}
            size="small"
          />
          <TextField
            label="Name"
            value={filters.name}
            onChange={handleFilterChange("name")}
            size="small"
          />
          <TextField
            label="Email"
            value={filters.email}
            onChange={handleFilterChange("email")}
            size="small"
          />
          <TextField
            label="Path"
            value={filters.path}
            onChange={handleFilterChange("path")}
            size="small"
          />
          <TextField
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={handleFilterChange("startDate")}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={handleFilterChange("endDate")}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
        {loading && logs.length === 0 ? (
          <Box display="flex" justifyContent="center" mt={4} mb={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            ref={containerRef}
            sx={{ maxHeight: 700, overflow: "auto", }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status Code</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Path</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{memoizedRows}</TableBody>
            </Table>
            {isScrollLoading && (
              <Box display="flex" justifyContent="center" mt={4} mb={4}>
                <CircularProgress />
              </Box>
            )}
          </TableContainer>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" px={3}>
        Total Count: {totalCount.toLocaleString()}
      </Box>
    </Card>
  );
};

export default UserActivityLogTable;